import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { z } from 'zod';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { LoginForm } from '@/components/auth/LoginForm';
import { redirectIfSignedIn } from '@/lib/use-session';

// `pendingInviteToken` is set when `/invites/$token` bounces a signed-out
// existing-account visitor here. LoginForm reads it and navigates back to
// /invites/$token after a successful sign-in so the invite redeems against
// the freshly-authed session (Case 3 of the email-first chooser flow).
//
// `from` is the legacy "bounce back to this URL after sign-in" param set
// by `_authed.tsx` and a couple of CTAs. No reader honors it yet, but
// existing call sites pass it — accept it here so the validator doesn't
// reject them.
//
// `reason` is set by the auth guards when the server (or the client-side
// mirror) identified a drift-final account state — the visitor was signed
// out because their account can never back a session again, and the form
// explains why instead of presenting a wordless login.
const loginSearchSchema = z.object({
  pendingInviteToken: z.string().min(1).optional(),
  from: z.string().optional(),
  reason: z.enum(['deactivated_expired', 'restricted']).optional(),
});

export const Route = createFileRoute('/auth/login')({
  validateSearch: loginSearchSchema.parse,
  beforeLoad: ({ context }) => redirectIfSignedIn(context),
  component: LoginPage,
});

function LoginPage() {
  const t = useTranslations('Auth');
  const { pendingInviteToken, reason } = Route.useSearch();
  return (
    <AuthLayout headline={t('layout.promoHeadline')}>
      <LoginForm
        {...(pendingInviteToken !== undefined ? { pendingInviteToken } : {})}
        {...(reason !== undefined ? { driftReason: reason } : {})}
      />
    </AuthLayout>
  );
}
